import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Text,
  HStack,
  Flex,
  useColorModeValue,
  Icon,
  Button,
  Badge,
  Spinner,
  VStack,
  Progress,
  Circle,
  keyframes,
} from '@chakra-ui/react';
import {
  FaChevronRight,
  FaTools,
  FaMapMarkerAlt,
  FaRocket,
  FaLightbulb,
  FaBolt,
  FaFire,
  FaTrophy,
  FaArrowRight,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getActions } from '../services/playbookService';
import { logger } from '../utils/logger';
import { useUser } from '../contexts/UserContext';

// Pulse animation for active items
const pulseKeyframes = keyframes`
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.05); opacity: 0.8; }
`;

// Shimmer animation for highlight effect
const shimmerKeyframes = keyframes`
  0% { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

// Glow animation
const glowKeyframes = keyframes`
  0%, 100% { box-shadow: 0 0 5px rgba(99, 102, 241, 0.3); }
  50% { box-shadow: 0 0 20px rgba(99, 102, 241, 0.5), 0 0 30px rgba(167, 139, 250, 0.3); }
`;

const ActionsPlaybookSnippet = ({ ctaOnly = false, compact = false }) => {
  const { favoriteUris } = useUser();
  const [actions, setActions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [hoveredAction, setHoveredAction] = useState(null);
  const navigate = useNavigate();

  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.900');
  const cardBg = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');
  const textColor = useColorModeValue('gray.800', 'gray.100');
  const mutedColor = useColorModeValue('gray.500', 'gray.400');
  const accentGradient = 'linear(to-r, #6366f1, #a78bfa, #ec4899)';
  const cardHoverBg = useColorModeValue('gray.100', 'gray.700');
  const inactiveBarColor = useColorModeValue('gray.200', 'gray.600');
  const progressBgColor = useColorModeValue('gray.200', 'gray.700');
  const emptyCircleBg = useColorModeValue('gray.200', 'gray.700');

  // Category configuration with vibrant colors
  const categoryConfig = {
    all: {
      label: 'All',
      icon: FaLightbulb,
      color: '#6366f1',
      gradient: 'linear(to-r, #6366f1, #8b5cf6)',
      bgLight: 'rgba(99, 102, 241, 0.1)',
    },
    operational_change: {
      label: 'Operations',
      icon: FaTools,
      color: '#3b82f6',
      gradient: 'linear(to-r, #3b82f6, #06b6d4)',
      bgLight: 'rgba(59, 130, 246, 0.1)',
    },
    competitive_move: {
      label: 'Compete',
      icon: FaRocket,
      color: '#8b5cf6',
      gradient: 'linear(to-r, #8b5cf6, #d946ef)',
      bgLight: 'rgba(139, 92, 246, 0.1)',
    },
    listing_improvement: {
      label: 'Visibility',
      icon: FaMapMarkerAlt,
      color: '#10b981',
      gradient: 'linear(to-r, #10b981, #34d399)',
      bgLight: 'rgba(16, 185, 129, 0.1)',
    },
  };

  // Priority/Impact visual config
  const getImpactConfig = (impact) => {
    const configs = {
      high: { icon: FaFire, color: '#ef4444', label: 'High Impact', bars: 3 },
      medium: { icon: FaBolt, color: '#f59e0b', label: 'Medium', bars: 2 },
      low: { icon: FaLightbulb, color: '#6b7280', label: 'Low', bars: 1 },
    };
    return configs[impact] || configs.medium;
  };

  // Fetch actions
  const fetchActions = useCallback(async () => {
    setIsLoading(true);
    try {
      const actionsData = await getActions({ status: 'pending' });
      const inProgressActions = await getActions({ status: 'in_progress' });
      const allActions = [...(actionsData || []), ...(inProgressActions || [])];
      setActions(allActions);
      logger.info('ActionsPlaybookSnippet: Loaded actions', { count: allActions.length });
    } catch (error) {
      logger.error('ActionsPlaybookSnippet: Error loading actions', error);
      setActions([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  // Filter actions by category (show all market actions, not just favorites)
  const filteredActions = actions.filter(action => {
    if (selectedCategory === 'all') return true;
    return action.type === selectedCategory;
  });

  // Sort by impact (high first) and status (in_progress first)
  const sortedActions = [...filteredActions].sort((a, b) => {
    const statusOrder = { in_progress: 0, pending: 1, completed: 2 };
    const impactOrder = { high: 0, medium: 1, low: 2 };
    if (statusOrder[a.status] !== statusOrder[b.status]) {
      return statusOrder[a.status] - statusOrder[b.status];
    }
    return (impactOrder[a.expected_impact] || 1) - (impactOrder[b.expected_impact] || 1);
  });

  const displayActions = sortedActions.slice(0, 4);

  const handleViewAll = () => {
    navigate('/playbook/actions');
  };

  // Get counts per category
  const getCategoryCount = (cat) => {
    if (cat === 'all') {
      return actions.length;
    }
    return actions.filter(a => a.type === cat).length;
  };

  // Calculate completion progress
  const totalActions = actions.length;
  const inProgressCount = actions.filter(a => a.status === 'in_progress').length;
  const progressPercent = totalActions > 0 ? Math.round((inProgressCount / totalActions) * 100) : 0;

  // Impact bars component
  const ImpactBars = ({ count, color }) => (
    <HStack spacing={0.5}>
      {[1, 2, 3].map((i) => (
        <Box
          key={i}
          w="3px"
          h={`${8 + i * 3}px`}
          borderRadius="full"
          bg={i <= count ? color : inactiveBarColor}
          transition="all 0.2s"
        />
      ))}
    </HStack>
  );

  // Single action item component
  const ActionItem = ({ action, index }) => {
    const config = categoryConfig[action.type] || categoryConfig.operational_change;
    const impactConfig = getImpactConfig(action.expected_impact);
    const isActive = action.status === 'in_progress';
    const isHovered = hoveredAction === action.action_id;

    return (
      <Flex
        onMouseEnter={() => setHoveredAction(action.action_id)}
        onMouseLeave={() => setHoveredAction(null)}
        onClick={handleViewAll}
        cursor="pointer"
        align="center"
        p={3}
        borderRadius="xl"
        bg={isHovered ? cardHoverBg : cardBg}
        borderWidth="1px"
        borderColor={isActive ? config.color : borderColor}
        position="relative"
        overflow="hidden"
        transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
        transform={isHovered ? 'translateX(4px)' : 'translateX(0)'}
        _before={isActive ? {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '3px',
          bgGradient: config.gradient,
          borderRadius: 'full',
        } : undefined}
        animation={isActive ? `${glowKeyframes} 2s infinite` : undefined}
      >
        {/* Category icon */}
        <Circle
          size="36px"
          bgGradient={config.gradient}
          mr={3}
          flexShrink={0}
          animation={isActive ? `${pulseKeyframes} 2s infinite` : undefined}
        >
          <Icon as={config.icon} color="white" boxSize={4} />
        </Circle>

        {/* Content */}
        <Box flex={1} minW={0}>
          <Text
            fontSize="sm"
            fontWeight="600"
            color={textColor}
            noOfLines={1}
            mb={0.5}
          >
            {action.title}
          </Text>
          <HStack spacing={2}>
            <ImpactBars count={impactConfig.bars} color={impactConfig.color} />
            <Text fontSize="xs" color={mutedColor}>
              {impactConfig.label}
            </Text>
          </HStack>
        </Box>

        {/* Status badge */}
        {isActive && (
          <Badge
            bgGradient="linear(to-r, #6366f1, #8b5cf6)"
            color="white"
            fontSize="2xs"
            px={2}
            py={0.5}
            borderRadius="full"
            fontWeight="700"
            textTransform="uppercase"
            letterSpacing="wider"
            ml={2}
          >
            Active
          </Badge>
        )}

        {/* Hover arrow */}
        <Icon
          as={FaChevronRight}
          color={mutedColor}
          boxSize={3}
          ml={2}
          opacity={isHovered ? 1 : 0}
          transform={isHovered ? 'translateX(0)' : 'translateX(-4px)'}
          transition="all 0.2s"
        />
      </Flex>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <Box
        bg={bgColor}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        overflow="hidden"
        h="100%"
        minH="400px"
      >
        <Box
          h="4px"
          bgGradient={accentGradient}
          backgroundSize="200% 100%"
          animation={`${shimmerKeyframes} 1.5s infinite`}
        />
        <Flex direction="column" align="center" justify="center" h="calc(100% - 4px)" p={6}>
          <Spinner size="lg" color="#6366f1" thickness="3px" mb={4} />
          <Text color={mutedColor} fontSize="sm" fontWeight="500">
            Loading your action plan...
          </Text>
        </Flex>
      </Box>
    );
  }

  // Empty state - show helpful message instead of hiding
  if (!actions.length) {
    return (
      <Box
        bg={bgColor}
        borderRadius="2xl"
        borderWidth="1px"
        borderColor={borderColor}
        overflow="hidden"
        h="100%"
        minH="400px"
        display="flex"
        flexDirection="column"
        transition="all 0.3s"
        _hover={{
          borderColor: '#6366f1',
          boxShadow: '0 8px 30px rgba(99, 102, 241, 0.1)',
        }}
      >
        {/* Accent top bar */}
        <Box h="4px" bgGradient={accentGradient} />

        {/* Content */}
        <Flex direction="column" align="center" justify="center" flex={1} p={6}>
          <Circle size="64px" bg="rgba(99, 102, 241, 0.1)" mb={4}>
            <Icon as={FaTrophy} color="#6366f1" boxSize={7} />
          </Circle>
          <Heading size="md" fontWeight="700" color={textColor} mb={2} textAlign="center">
            Beat Competitors
          </Heading>
          <Text fontSize="sm" color={mutedColor} textAlign="center" maxW="280px" mb={4}>
            AI-powered actions will appear here to help you outperform the competition
          </Text>
          <Button
            size="md"
            bgGradient={accentGradient}
            color="white"
            fontWeight="600"
            borderRadius="xl"
            rightIcon={<Icon as={FaArrowRight} boxSize={4} />}
            onClick={handleViewAll}
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.2s"
          >
            View Actions
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      bg={bgColor}
      borderRadius="2xl"
      borderWidth="1px"
      borderColor={borderColor}
      overflow="hidden"
      h="100%"
      display="flex"
      flexDirection="column"
      transition="all 0.3s"
      _hover={{
        borderColor: '#6366f1',
        boxShadow: '0 8px 30px rgba(99, 102, 241, 0.1)',
      }}
    >
      {/* Accent top bar */}
      <Box h="4px" bgGradient={accentGradient} />

      {/* Header */}
      <Box px={5} pt={5} pb={4}>
        <Flex justify="space-between" align="flex-start" mb={4}>
          <Box>
            <HStack spacing={2} mb={1}>
              <Circle size="28px" bgGradient={accentGradient}>
                <Icon as={FaTrophy} color="white" boxSize={3} />
              </Circle>
              <Heading size="md" fontWeight="700" color={textColor}>
                Beat Competitors
              </Heading>
            </HStack>
            <Text fontSize="xs" color={mutedColor} pl="36px">
              AI-powered actions to outperform the competition
            </Text>
          </Box>
          <Button
            size="xs"
            variant="ghost"
            color="#6366f1"
            rightIcon={<Icon as={FaArrowRight} boxSize={3} />}
            onClick={handleViewAll}
            fontWeight="600"
            _hover={{ bg: 'rgba(99, 102, 241, 0.1)' }}
          >
            View All
          </Button>
        </Flex>

        {/* Progress indicator */}
        {inProgressCount > 0 && (
          <Box
            bg={cardBg}
            borderRadius="xl"
            p={3}
            mb={4}
          >
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="xs" fontWeight="600" color={textColor}>
                {inProgressCount} action{inProgressCount !== 1 ? 's' : ''} in progress
              </Text>
              <Text fontSize="xs" fontWeight="700" color="#6366f1">
                {progressPercent}%
              </Text>
            </Flex>
            <Progress
              value={progressPercent}
              size="sm"
              borderRadius="full"
              bg={progressBgColor}
              sx={{
                '& > div': {
                  background: 'linear-gradient(to right, #6366f1, #a78bfa, #ec4899)',
                  borderRadius: 'full',
                },
              }}
            />
          </Box>
        )}

        {/* Category pills */}
        <HStack spacing={2} overflowX="auto" pb={1}>
          {Object.entries(categoryConfig).map(([key, config]) => {
            const count = getCategoryCount(key);
            const isSelected = selectedCategory === key;
            
            return (
              <Flex
                key={key}
                align="center"
                px={3}
                py={1.5}
                borderRadius="full"
                cursor="pointer"
                onClick={() => setSelectedCategory(key)}
                bg={isSelected ? config.bgLight : 'transparent'}
                borderWidth="1px"
                borderColor={isSelected ? config.color : borderColor}
                transition="all 0.2s"
                _hover={{
                  borderColor: config.color,
                  bg: config.bgLight,
                }}
                flexShrink={0}
              >
                <Icon
                  as={config.icon}
                  boxSize={3}
                  color={isSelected ? config.color : mutedColor}
                  mr={1.5}
                />
                <Text
                  fontSize="xs"
                  fontWeight={isSelected ? '600' : '500'}
                  color={isSelected ? config.color : mutedColor}
                >
                  {config.label}
                </Text>
                <Badge
                  ml={1.5}
                  bg={isSelected ? config.color : inactiveBarColor}
                  color={isSelected ? 'white' : mutedColor}
                  fontSize="2xs"
                  px={1.5}
                  borderRadius="full"
                  fontWeight="700"
                >
                  {count}
                </Badge>
              </Flex>
            );
          })}
        </HStack>
      </Box>

      {/* Actions list */}
      <Box px={5} pb={5} flex={1} display="flex" flexDirection="column">
        {displayActions.length > 0 ? (
          <VStack spacing={2} align="stretch" flex={1}>
            {displayActions.map((action, index) => (
              <ActionItem key={action.action_id} action={action} index={index} />
            ))}
          </VStack>
        ) : (
          <Flex
            direction="column"
            align="center"
            justify="center"
            flex={1}
            py={6}
            bg={cardBg}
            borderRadius="xl"
          >
            <Circle size="48px" bg={emptyCircleBg} mb={3}>
              <Icon as={FaLightbulb} color={mutedColor} boxSize={5} />
            </Circle>
            <Text fontSize="sm" color={mutedColor} fontWeight="500" textAlign="center">
              No {selectedCategory === 'all' ? '' : categoryConfig[selectedCategory]?.label.toLowerCase() + ' '}actions available
            </Text>
            <Text fontSize="xs" color={mutedColor} mt={1}>
              Check back soon for new recommendations
            </Text>
          </Flex>
        )}

        {/* CTA Button */}
        {(ctaOnly || displayActions.length > 0) && (
          <Button
            mt={4}
            w="100%"
            size="md"
            bgGradient={accentGradient}
            color="white"
            fontWeight="600"
            borderRadius="xl"
            rightIcon={<Icon as={FaArrowRight} boxSize={4} />}
            onClick={handleViewAll}
            _hover={{
              transform: 'translateY(-2px)',
              boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)',
            }}
            _active={{
              transform: 'translateY(0)',
            }}
            transition="all 0.2s"
          >
            View All Actions
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ActionsPlaybookSnippet;
